import React, { useEffect, useMemo, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import { View, Text, TextInput, StyleSheet, Alert, Platform, Modal, FlatList, TouchableOpacity, ScrollView, KeyboardAvoidingView } from 'react-native';
import theme from '../../../theme';
import { createTransaction, fetchCategories, newTransactionId, updateTransaction } from '../../transactions/transactionsRepository';
import { useI18n } from '../../../i18n/useI18n';
import CategoriesManager from './CategoriesManager';
import AppButton from '../../../components/AppButton';

type Category = {
  id: string;
  name: string;
  type: 'income' | 'expense';
  color?: string | null;
  user_id?: string | null;
};

function toLocalYmd(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromLocalYmd(txt: string) {
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(txt.trim());
  if (!m) return undefined;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  if (Number.isNaN(dt.getTime())) return undefined;
  return dt;
}

function formatDisplayDate(d: Date, locale: string | undefined) {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return (locale === 'es') ? `${dd}/${mm}/${yyyy}` : `${mm}/${dd}/${yyyy}`;
}

export default function TransactionForm({
  userId,
  onClose,
  defaultType = 'expense',
  lang,
  presentation = 'card',
  initialTransaction,
}: {
  userId?: string;
  onClose: () => void;
  defaultType?: 'expense' | 'income';
  lang?: string;
  presentation?: 'card' | 'fullscreen';
  initialTransaction?: any;
}) {
  const [type, setType] = useState<'expense'|'income'>(defaultType as any);
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [showCategoriesManager, setShowCategoriesManager] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showWebDateInput, setShowWebDateInput] = useState(false);
  const webDateInputRef = React.useRef<any>(null);
  const { t } = useI18n(lang);
  const { width } = useWindowDimensions();

  const isWide = width >= 700;

  // build containerStyle as an Array<any> to avoid ViewStyle typing conflicts
  const containerStyle: Array<any> = [styles.container];
  if (presentation === 'fullscreen') {
    containerStyle.push(styles.fullscreen);
    if (!isWide) {
      containerStyle.push({ width: '100%', maxWidth: undefined, borderRadius: theme.radii.sm, paddingHorizontal: 16 } as any);
    } else {
      containerStyle.push({ alignSelf: 'center', maxWidth: 420 } as any);
    }
  } else {
    containerStyle.push({ alignSelf: 'center', maxWidth: 420 } as any);
  }

  useEffect(() => {
    if (!initialTransaction) return;
    try {
      if (initialTransaction.type) setType(initialTransaction.type);
      if (initialTransaction.amount != null) setAmount(String(initialTransaction.amount));
      setCategory(String(initialTransaction.category || ''));
      setNotes(String(initialTransaction.notes || ''));
      if (initialTransaction.date) {
        const d = new Date(initialTransaction.date);
        if (!Number.isNaN(d.getTime())) setDate(d);
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTransaction?.id]);

  async function loadCategories() {
    if (!userId) return;
    const data = await fetchCategories(userId);
    setCategories((data || []) as Category[]);
  }

  useEffect(() => {
    loadCategories().catch(() => {});
  }, [userId]);

  // If type changes, clear selected category if it doesn't match.
  useEffect(() => {
    if (selectedCategory && selectedCategory.type !== type) {
      setSelectedCategory(null);
      setCategory('');
    }
  }, [type]);

  const visibleCategories = useMemo(() => {
    return (categories || []).filter(c => c.type === type);
  }, [categories, type]);

  async function handleSave() {
    const a = parseFloat(amount);
    if (!userId) return Alert.alert('No user');
    if (!a || a <= 0) return Alert.alert(t('amount') + ' invalid');
    try {
      const payload = {
        amount: a,
        type,
        category: (selectedCategory?.name || category || (type === 'expense' ? 'Misc' : 'Income')),
        category_id: selectedCategory?.id,
        date: date.toISOString(),
        notes,
        user_id: userId,
      };

      if (initialTransaction && initialTransaction.id) {
        await updateTransaction(String(initialTransaction.id), payload as any);
      } else {
        await createTransaction({ id: newTransactionId(), ...(payload as any) } as any);
      }
      onClose();
    } catch (e) {
      Alert.alert('Error', (e as any).message || String(e));
    }
  }

  return (
    <View
      style={[
        ...containerStyle,
        { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
      ]}
    > 
      {showCategoriesManager && userId ? (
        <CategoriesManager
          userId={userId}
          lang={lang}
          // Always use modal so it matches the main screen and avoids a narrower inline grid on wide layouts.
          presentation="modal"
          onClose={() => {
            setShowCategoriesManager(false);
            loadCategories().catch(() => {});
          }}
        />
      ) : (
        presentation === 'fullscreen' ? (
          <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
            <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={{ paddingBottom: 24 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text style={[styles.title, { color: theme.colors.textPrimary }]}>
                {initialTransaction ? `${t('edit')} ${type==='expense' ? t('expense') : t('income')}` : (type==='expense'?t('addExpense'):t('addIncome'))}
                </Text>
                <AppButton title={t('cancel')} onPress={onClose} tone="secondary" variant="outline" style={{ minHeight: 34, paddingVertical: 6 }} />
              </View>
          <View style={{height:8}} />
          <Text style={styles.label}>{t('amount')}</Text>
          <TextInput keyboardType="numeric" value={amount} onChangeText={setAmount} style={styles.input} />
          <Text style={styles.label}>{t('category')}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <View style={{ flex: 1 }}>
              <TextInput
                value={selectedCategory ? selectedCategory.name : category}
                onChangeText={(v) => { setSelectedCategory(null); setCategory(v); }}
                style={styles.input}
                placeholder={t('selectCategory')}
                placeholderTextColor={theme.colors.placeholder}
              />
            </View>
            <View style={{ width: 8 }} />
            <AppButton title={t('choose')} onPress={() => setShowCategoryPicker(true)} tone="secondary" variant="outline" />
          </View>
          <View style={{ height: 8 }} />
          <AppButton title={t('manageCategories')} onPress={() => setShowCategoriesManager(true)} tone="secondary" />

          {showCategoryPicker ? (
            isWide ? (
              <View style={[styles.pickerCardWrapper, { backgroundColor: theme.colors.background }]}> 
                <View style={[styles.pickerCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}> 
                  <Text style={[styles.pickerTitle, { color: theme.colors.textPrimary }]}>{t('selectCategory')}</Text>
                  <View style={{ height: 8 }} />
                  <AppButton title={t('close')} onPress={() => setShowCategoryPicker(false)} tone="secondary" variant="outline" />
                  <View style={{ height: 12 }} />
                  <FlatList
                    data={visibleCategories}
                    keyExtractor={(item) => item.id}
                    ListEmptyComponent={<Text style={{ color: theme.colors.textSecondary }}>{t('noCategories')}</Text>}
                    renderItem={({ item }) => (
                      <TouchableOpacity
                        onPress={() => {
                          setSelectedCategory(item);
                          setCategory('');
                          setShowCategoryPicker(false);
                        }}
                        style={[styles.pickerItem, { borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                          {!!item.color && (
                            <View
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: 5,
                                marginRight: 8,
                                backgroundColor: item.color,
                                borderWidth: 1,
                                borderColor: theme.colors.border,
                              }}
                            />
                          )}
                          <Text style={{ color: theme.colors.textPrimary, fontWeight: '700' }}>
                            {item.name}
                          </Text>
                        </View>
                        <Text style={{ color: theme.colors.textSecondary }}>{item.type}</Text>
                      </TouchableOpacity>
                    )}
                  />
                </View>
              </View>
            ) : (
              <Modal visible={showCategoryPicker} animationType="slide">
                <View style={[styles.pickerContainer, { backgroundColor: theme.colors.background }]}>
                  <Text style={[styles.pickerTitle, { color: theme.colors.textPrimary }]}>{t('selectCategory')}</Text>
                  <View style={{ height: 8 }} />
                  <AppButton title={t('close')} onPress={() => setShowCategoryPicker(false)} tone="secondary" variant="outline" />
                  <View style={{ height: 12 }} />
                  <FlatList
                    data={visibleCategories}
                    keyExtractor={(item) => item.id}
                    ListEmptyComponent={<Text style={{ color: theme.colors.textSecondary }}>{t('noCategories')}</Text>}
                    renderItem={({ item }) => (
                      <TouchableOpacity
                        onPress={() => {
                          setSelectedCategory(item);
                          setCategory('');
                          setShowCategoryPicker(false);
                        }}
                        style={[styles.pickerItem, { borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                          {!!item.color && (
                            <View
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: 5,
                                marginRight: 8,
                                backgroundColor: item.color,
                                borderWidth: 1,
                                borderColor: theme.colors.border,
                              }}
                            />
                          )}
                          <Text style={{ color: theme.colors.textPrimary, fontWeight: '700' }}>
                            {item.name}
                          </Text>
                        </View>
                        <Text style={{ color: theme.colors.textSecondary }}>{item.type}</Text>
                      </TouchableOpacity>
                    )}
                  />
                </View>
              </Modal>
            )
          ) : null}

          <Text style={styles.label}>{t('date')}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            <Text style={{ color: theme.colors.textSecondary, marginRight: 8 }}>{formatDisplayDate(date, lang)}</Text>
            <AppButton title={t('today')} onPress={() => setDate(new Date())} tone="secondary" variant="outline" />
            <View style={{ width: 8, height: 8 }} />
            <AppButton
              title={t('changeDate')}
              onPress={() => {
                if (Platform.OS === 'web') setShowWebDateInput(v => !v);
                else setShowDatePicker(true);
              }}
              tone="secondary"
            />
          </View>

          {Platform.OS === 'web' ? (
            <>
              {/* Hidden native input so we can programmatically open the browser date picker */}
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              <input
                ref={(el) => { (webDateInputRef as any).current = el; }}
                type="date"
                value={toLocalYmd(date)}
                onChange={(e: any) => {
                  const txt = e?.target?.value || '';
                  const d = fromLocalYmd(txt);
                  if (d) setDate(d);
                }}
                style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }}
              />
              {showWebDateInput ? (
                <View style={{ marginBottom: 8 }}>
                  {/* visible editable date input */}
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                  {/* @ts-ignore */}
                  <input
                    type="date"
                    value={toLocalYmd(date)}
                    onChange={(e: any) => {
                      const txt = e?.target?.value || '';
                      const d = fromLocalYmd(txt);
                      if (d) setDate(d);
                    }}
                    style={{
                      height: 40,
                      paddingLeft: 8,
                      paddingRight: 8,
                      borderRadius: 4,
                      width: '100%',
                      boxSizing: 'border-box',
                      color: theme.colors.textPrimary,
                      background: theme.colors.background,
                      borderColor: theme.colors.border,
                      borderWidth: 1,
                      borderStyle: 'solid',
                    }}
                  />
                </View>
              ) : null}
            </>
          ) : (
            (() => {
              try {
                // eslint-disable-next-line @typescript-eslint/no-var-requires
                const NativePicker = require('@react-native-community/datetimepicker').default;

                if (!showDatePicker) return null;

                if (Platform.OS === 'ios') {
                  return (
                    <Modal transparent animationType="fade" visible={showDatePicker} onRequestClose={() => setShowDatePicker(false)}>
                      <View style={styles.dateModalBackdrop}>
                        <View style={[styles.dateModalCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
                          <Text style={[styles.dateModalTitle, { color: theme.colors.textPrimary }]}>{t('selectDate')}</Text>
                          <View style={{ height: 8 }} />
                          <NativePicker
                            value={date}
                            mode="date"
                            display="default"
                            onChange={(e: any, d: Date | undefined) => {
                              if (d) setDate(d);
                              // close after picking to keep it "tap -> pick -> done"
                              if (d) setShowDatePicker(false);
                            }}
                          />
                          <View style={{ height: 12 }} />
                          <AppButton title={t('close')} onPress={() => setShowDatePicker(false)} tone="secondary" variant="outline" />
                        </View>
                      </View>
                    </Modal>
                  );
                }

                // Android: DateTimePicker shows a native dialog; unmount after change/dismiss.
                return (
                  <NativePicker
                    value={date}
                    mode="date"
                    display="calendar"
                    onChange={(e: any, d: Date | undefined) => {
                      setShowDatePicker(false);
                      // Android emits a dismiss event with no date; ignore it.
                      if (d) setDate(d);
                    }}
                  />
                );
              } catch {
                return null;
              }
            })()
          )}
          <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('notes')}</Text>
          <TextInput value={notes} onChangeText={setNotes} style={[styles.input, { color: theme.colors.textPrimary, backgroundColor: theme.colors.background, borderColor: theme.colors.border }]} />
          <View style={{height:12}} />
          <View style={{flexDirection:'row'}}>
            <AppButton title={t('cancel')} onPress={onClose} tone="secondary" variant="outline" style={{ flex: 1 }} />
            <View style={{width:12}} />
            <AppButton title={t('save')} onPress={handleSave} tone="primary" style={{ flex: 1 }} />
          </View>
            </ScrollView>
          </KeyboardAvoidingView>
        ) : (
          <>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={[styles.title, { color: theme.colors.textPrimary }]}>
              {initialTransaction ? `${t('edit')} ${type==='expense' ? t('expense') : t('income')}` : (type==='expense'?t('addExpense'):t('addIncome'))}
              </Text>
            </View>
        <View style={{height:8}} />
        <Text style={styles.label}>{t('amount')}</Text>
        <TextInput keyboardType="numeric" value={amount} onChangeText={setAmount} style={styles.input} />
        <Text style={styles.label}>{t('category')}</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View style={{ flex: 1 }}>
            <TextInput
              value={selectedCategory ? selectedCategory.name : category}
              onChangeText={(v) => { setSelectedCategory(null); setCategory(v); }}
              style={styles.input}
              placeholder={t('selectCategory')}
              placeholderTextColor={theme.colors.placeholder}
            />
          </View>
          <View style={{ width: 8 }} />
          <AppButton title={t('choose')} onPress={() => setShowCategoryPicker(true)} tone="secondary" variant="outline" />
        </View>
        <View style={{ height: 8 }} />
        <AppButton title={t('manageCategories')} onPress={() => setShowCategoriesManager(true)} tone="secondary" />

        {showCategoryPicker ? (
          isWide ? (
            <View style={[styles.pickerCardWrapper, { backgroundColor: theme.colors.background }]}> 
              <View style={[styles.pickerCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}> 
                <Text style={[styles.pickerTitle, { color: theme.colors.textPrimary }]}>{t('selectCategory')}</Text>
                <View style={{ height: 8 }} />
                <AppButton title={t('close')} onPress={() => setShowCategoryPicker(false)} tone="secondary" variant="outline" />
                <View style={{ height: 12 }} />
                <FlatList
                  data={visibleCategories}
                  keyExtractor={(item) => item.id}
                  ListEmptyComponent={<Text style={{ color: theme.colors.textSecondary }}>{t('noCategories')}</Text>}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      onPress={() => {
                        setSelectedCategory(item);
                        setCategory('');
                        setShowCategoryPicker(false);
                      }}
                      style={[styles.pickerItem, { borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        {!!item.color && (
                          <View
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 5,
                              marginRight: 8,
                              backgroundColor: item.color,
                              borderWidth: 1,
                              borderColor: theme.colors.border,
                            }}
                          />
                        )}
                        <Text style={{ color: theme.colors.textPrimary, fontWeight: '700' }}>
                          {item.name}
                        </Text>
                      </View>
                      <Text style={{ color: theme.colors.textSecondary }}>{item.type}</Text>
                    </TouchableOpacity>
                  )}
                />
              </View>
            </View>
          ) : (
            <Modal visible={showCategoryPicker} animationType="slide">
              <View style={[styles.pickerContainer, { backgroundColor: theme.colors.background }]}>
                <Text style={[styles.pickerTitle, { color: theme.colors.textPrimary }]}>{t('selectCategory')}</Text>
                <View style={{ height: 8 }} />
                <AppButton title={t('close')} onPress={() => setShowCategoryPicker(false)} tone="secondary" variant="outline" />
                <View style={{ height: 12 }} />
                <FlatList
                  data={visibleCategories}
                  keyExtractor={(item) => item.id}
                  ListEmptyComponent={<Text style={{ color: theme.colors.textSecondary }}>{t('noCategories')}</Text>}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      onPress={() => {
                        setSelectedCategory(item);
                        setCategory('');
                        setShowCategoryPicker(false);
                      }}
                      style={[styles.pickerItem, { borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        {!!item.color && (
                          <View
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 5,
                              marginRight: 8,
                              backgroundColor: item.color,
                              borderWidth: 1,
                              borderColor: theme.colors.border,
                            }}
                          />
                        )}
                        <Text style={{ color: theme.colors.textPrimary, fontWeight: '700' }}>
                          {item.name}
                        </Text>
                      </View>
                      <Text style={{ color: theme.colors.textSecondary }}>{item.type}</Text>
                    </TouchableOpacity>
                  )}
                />
              </View>
            </Modal>
          )
        ) : null}

        <Text style={styles.label}>{t('date')}</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <Text style={{ color: theme.colors.textSecondary, marginRight: 8 }}>{formatDisplayDate(date, lang)}</Text>
          <AppButton title={t('today')} onPress={() => setDate(new Date())} tone="secondary" variant="outline" />
          <View style={{ width: 8, height: 8 }} />
          <AppButton
            title={t('changeDate')}
            onPress={() => {
              if (Platform.OS === 'web') setShowWebDateInput(v => !v);
              else setShowDatePicker(true);
            }}
            tone="secondary"
          />
        </View>

        {Platform.OS === 'web' ? (
          <>
            {/* Hidden native input so we can programmatically open the browser date picker */}
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <input
              ref={(el) => { (webDateInputRef as any).current = el; }}
              type="date"
              value={toLocalYmd(date)}
              onChange={(e: any) => {
                const txt = e?.target?.value || '';
                const d = fromLocalYmd(txt);
                if (d) setDate(d);
              }}
              style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }}
            />
            {showWebDateInput ? (
              <View style={{ marginBottom: 8 }}>
                {/* visible editable date input */}
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore */}
                <input
                  type="date"
                  value={toLocalYmd(date)}
                  onChange={(e: any) => {
                    const txt = e?.target?.value || '';
                    const d = fromLocalYmd(txt);
                    if (d) setDate(d);
                  }}
                  style={{
                    height: 40,
                    paddingLeft: 8,
                    paddingRight: 8,
                    borderRadius: 4,
                    width: '100%',
                    boxSizing: 'border-box',
                    color: theme.colors.textPrimary,
                    background: theme.colors.background,
                    borderColor: theme.colors.border,
                    borderWidth: 1,
                    borderStyle: 'solid',
                  }}
                />
              </View>
            ) : null}
          </>
        ) : (
          (() => {
            try {
              // eslint-disable-next-line @typescript-eslint/no-var-requires
              const NativePicker = require('@react-native-community/datetimepicker').default;

              if (!showDatePicker) return null;

              if (Platform.OS === 'ios') {
                return (
                  <Modal transparent animationType="fade" visible={showDatePicker} onRequestClose={() => setShowDatePicker(false)}>
                    <View style={styles.dateModalBackdrop}>
                      <View style={[styles.dateModalCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
                        <Text style={[styles.dateModalTitle, { color: theme.colors.textPrimary }]}>{t('selectDate')}</Text>
                        <View style={{ height: 8 }} />
                        <NativePicker
                          value={date}
                          mode="date"
                          display="default"
                          onChange={(e: any, d: Date | undefined) => {
                            if (d) setDate(d);
                            // close after picking to keep it "tap -> pick -> done"
                            if (d) setShowDatePicker(false);
                          }}
                        />
                        <View style={{ height: 12 }} />
                        <AppButton title={t('close')} onPress={() => setShowDatePicker(false)} tone="secondary" variant="outline" />
                      </View>
                    </View>
                  </Modal>
                );
              }

              // Android: DateTimePicker shows a native dialog; unmount after change/dismiss.
              return (
                <NativePicker
                  value={date}
                  mode="date"
                  display="calendar"
                  onChange={(e: any, d: Date | undefined) => {
                    setShowDatePicker(false);
                    // Android emits a dismiss event with no date; ignore it.
                    if (d) setDate(d);
                  }}
                />
              );
            } catch {
              return null;
            }
          })()
        )}
        <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('notes')}</Text>
        <TextInput value={notes} onChangeText={setNotes} style={[styles.input, { color: theme.colors.textPrimary, backgroundColor: theme.colors.background, borderColor: theme.colors.border }]} />
        <View style={{height:12}} />
        <View style={{flexDirection:'row'}}>
          <AppButton title={t('cancel')} onPress={onClose} tone="secondary" variant="outline" style={{ flex: 1 }} />
          <View style={{width:12}} />
          <AppButton title={t('save')} onPress={handleSave} tone="primary" style={{ flex: 1 }} />
        </View>
          </>
        )
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { padding: 12, borderRadius: theme.radii.sm, borderWidth: 1 },
  fullscreen: { flex: 1, borderRadius: 0, borderWidth: 0, padding: 16 },
  title: { fontSize: 18, fontWeight: '700' },
  label: { marginTop: 8, marginBottom: 4, fontWeight: '600' },
  input: { height: 40, borderWidth: 1, paddingHorizontal: 8, borderRadius: 4 },
  pickerContainer: { flex: 1, padding: 16 },
  pickerTitle: { fontSize: 18, fontWeight: '800' },
  pickerItem: { padding: 12, borderRadius: 8, borderWidth: 1, marginBottom: 10 },
  dateModalBackdrop: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16, backgroundColor: 'rgba(0,0,0,0.35)' },
  dateModalCard: { width: '100%', maxWidth: 420, borderRadius: 12, padding: 16, borderWidth: 1 },
  dateModalTitle: { fontSize: 16, fontWeight: '800' },
  pickerCardWrapper: { alignItems: 'center', marginTop: 12 },
  pickerCard: { width: '100%', maxWidth: 420, borderRadius: 12, padding: 16, borderWidth: 1 },
});
